import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../api";
import { useAuth } from "../AuthContext";
import { useI18n } from "../I18nContext";

const AUTHOR_ROLES = ["advisor", "accelerator", "investor", "provider", "admin"];

const slugify = (s) =>
  (s || "").toLowerCase().trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);

const emptyDoc = () => ({
  slug: "",
  title_en: "", title_fr: "",
  description_en: "", description_fr: "",
  visibility: "private",
  stages: [
    { key: "todo", name_en: "Not started", name_fr: "Non démarré" },
    { key: "in_progress", name_en: "In progress", name_fr: "En cours" },
    { key: "validated", name_en: "Validated", name_fr: "Validé" },
  ],
  dimensions: [],
  step_forms: {},
  references: [],
  legal_notice_en: "", legal_notice_fr: "",
});

export default function FrameworkBuilder() {
  const { id } = useParams();
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const isNew = !id;
  const [doc, setDoc] = useState(isNew ? emptyDoc() : null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (isNew) return;
    api.get("/frameworks")
      .then((r) => {
        const found = r.data.find((f) => f.id === id);
        if (!found) setErr(t("fwBuilder.notFound") || "Framework not found");
        else setDoc({ ...emptyDoc(), ...found });
      })
      .catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, [id, isNew, t]);

  if (!user) return <div className="tb-loading">{t("common.loading")}</div>;
  if (!AUTHOR_ROLES.includes(user.role)) {
    return (
      <div className="tb-page">
        <h1 className="tb-h1">{t("fwBuilder.title")}</h1>
        <p className="tb-muted">{t("fwBuilder.roleNotAllowed")}</p>
      </div>
    );
  }
  if (!doc) return <div className="tb-loading">{t("common.loading")}</div>;

  const canEdit = isNew || user.role === "admin" || doc.owner_id === user.id;
  const L = lang; // "en" | "fr"

  // Lang-aware getters/setters
  const set = (k, v) => setDoc({ ...doc, [k]: v });
  const getL = (obj, base) => (obj[`${base}_${L}`] ?? "");
  const setL = (base, v) => set(`${base}_${L}`, v);

  // ----- sections -----
  const addSection = () => {
    const key = slugify(`section-${doc.dimensions.length + 1}`);
    const sec = { key, attributes: [] };
    sec[`name_${L}`] = "";
    sec[`name_${L === "en" ? "fr" : "en"}`] = "";
    set("dimensions", [...doc.dimensions, sec]);
  };
  const updSection = (i, patch) => {
    const x = [...doc.dimensions];
    x[i] = { ...x[i], ...patch };
    set("dimensions", x);
  };
  const delSection = (i) => {
    if (!window.confirm(t("fwBuilder.confirmDeleteSection"))) return;
    set("dimensions", doc.dimensions.filter((_, idx) => idx !== i));
  };

  // ----- items -----
  const addItem = (si) => {
    const x = [...doc.dimensions];
    const itemKey = slugify(`item-${x[si].attributes.length + 1}`);
    const it = { key: itemKey };
    it[`name_${L}`] = "";
    it[`name_${L === "en" ? "fr" : "en"}`] = "";
    x[si] = { ...x[si], attributes: [...x[si].attributes, it] };
    set("dimensions", x);
  };
  const updItem = (si, ii, patch) => {
    const x = [...doc.dimensions];
    const atts = [...x[si].attributes];
    atts[ii] = { ...atts[ii], ...patch };
    x[si] = { ...x[si], attributes: atts };
    set("dimensions", x);
  };
  const delItem = (si, ii) => {
    if (!window.confirm(t("fwBuilder.confirmDeleteItem"))) return;
    const x = [...doc.dimensions];
    x[si] = { ...x[si], attributes: x[si].attributes.filter((_, idx) => idx !== ii) };
    set("dimensions", x);
  };

  // ----- questions -----
  const stepKey = (si, ii) => `${doc.dimensions[si].key}::${doc.dimensions[si].attributes[ii].key}`;
  const itemForm = (si, ii) => doc.step_forms[stepKey(si, ii)] || { questions: [] };
  const updateForm = (si, ii, patch) => {
    const k = stepKey(si, ii);
    set("step_forms", { ...doc.step_forms, [k]: { ...itemForm(si, ii), ...patch } });
  };
  const addQuestion = (si, ii) => {
    const f = itemForm(si, ii);
    const q = { key: `q${(f.questions || []).length + 1}` };
    q[`label_${L}`] = "";
    q[`label_${L === "en" ? "fr" : "en"}`] = "";
    updateForm(si, ii, { questions: [...(f.questions || []), q] });
  };
  const updQuestion = (si, ii, qi, patch) => {
    const f = itemForm(si, ii);
    const qs = [...(f.questions || [])];
    qs[qi] = { ...qs[qi], ...patch };
    updateForm(si, ii, { questions: qs });
  };
  const delQuestion = (si, ii, qi) => {
    const f = itemForm(si, ii);
    updateForm(si, ii, { questions: (f.questions || []).filter((_, idx) => idx !== qi) });
  };

  // ----- references -----
  const addRef = () => set("references", [...(doc.references || []), { label: "", url: "" }]);
  const updRef = (i, patch) => {
    const x = [...(doc.references || [])];
    x[i] = { ...x[i], ...patch };
    set("references", x);
  };
  const delRef = (i) => set("references", (doc.references || []).filter((_, idx) => idx !== i));

  const save = async () => {
    setBusy(true); setErr("");
    try {
      const payload = {
        slug: doc.slug || slugify(getL(doc, "title")),
        title_en: doc.title_en || "",
        title_fr: doc.title_fr || "",
        description_en: doc.description_en || "",
        description_fr: doc.description_fr || "",
        stages: doc.stages,
        dimensions: doc.dimensions,
        cells: doc.cells || {},
        step_forms: doc.step_forms || {},
        visibility: doc.visibility || "private",
        references: (doc.references || []).filter((r) => r.url),
        legal_notice_en: doc.legal_notice_en || "",
        legal_notice_fr: doc.legal_notice_fr || "",
      };
      if (!getL(doc, "title").trim()) { setErr(t("fwBuilder.titleRequired")); setBusy(false); return; }
      if (!payload.slug) { setErr(t("fwBuilder.slugRequired")); setBusy(false); return; }
      const r = isNew
        ? await api.post("/frameworks", payload)
        : await api.put(`/frameworks/${id}`, payload);
      navigate(`/app/frameworks/build/${r.data.id}`, { replace: true });
      setDoc({ ...emptyDoc(), ...r.data });
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(t("fwBuilder.confirmDeleteFramework"))) return;
    setBusy(true); setErr("");
    try {
      await api.delete(`/frameworks/${id}`);
      navigate("/app/frameworks");
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const langLabel = L === "fr" ? "Français" : "English";

  return (
    <div className="tb-page" data-testid="fw-builder">
      <Link to="/app/frameworks" className="tb-muted" style={{ fontSize: "0.85rem" }}>← {t("common.back")}</Link>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, flexWrap: "wrap", marginTop: 8 }}>
        <h1 className="tb-h1">{isNew ? t("fwBuilder.titleNew") : t("fwBuilder.titleEdit")}</h1>
        <span className="tb-badge accent" data-testid="fw-builder-lang">{t("fwBuilder.editingIn")} {langLabel}</span>
      </div>
      <p className="tb-muted" style={{ fontSize: "0.82rem", marginTop: 4 }}>{t("fwBuilder.langHint")}</p>

      {err && <div className="tb-form-error" style={{ marginBottom: 12 }} data-testid="fw-err">{err}</div>}

      <div className="tb-block">
        <h3 style={{ marginTop: 0 }}>{t("fwBuilder.basics")}</h3>
        <div className="tb-form">
          <div><label>{t("fwBuilder.titleLabel")}</label><input type="text" value={getL(doc, "title")} onChange={(e) => setL("title", e.target.value)} disabled={!canEdit} data-testid="fw-title" /></div>
          <div><label>{t("fwBuilder.slug")}</label><input type="text" value={doc.slug} onChange={(e) => set("slug", slugify(e.target.value))} placeholder={slugify(getL(doc, "title")) || "my-framework"} disabled={!canEdit} data-testid="fw-slug" /></div>
          <div><label>{t("fwBuilder.descLabel")}</label><textarea rows={3} value={getL(doc, "description")} onChange={(e) => setL("description", e.target.value)} disabled={!canEdit} data-testid="fw-desc" /></div>
          <div>
            <label>{t("fwBuilder.visibility")}</label>
            <select value={doc.visibility} onChange={(e) => set("visibility", e.target.value)} disabled={!canEdit} data-testid="fw-visibility">
              <option value="private">{t("fwBuilder.visPrivate")}</option>
              <option value="connections">{t("fwBuilder.visConnections")}</option>
              <option value="public">{t("fwBuilder.visPublic")}</option>
            </select>
          </div>
        </div>
      </div>

      <div className="tb-block" style={{ marginTop: 14 }}>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <h3 style={{ marginTop: 0 }}>{t("fwBuilder.sections")}</h3>
          {canEdit && <button type="button" className="tb-ghost-btn" onClick={addSection} data-testid="fw-add-section">+ {t("fwBuilder.addSection")}</button>}
        </div>
        {doc.dimensions.length === 0 && <p className="tb-muted">{t("fwBuilder.noSections")}</p>}
        {doc.dimensions.map((sec, si) => (
          <div key={si} className="tb-block" style={{ marginTop: 10 }} data-testid={`fw-section-${si}`}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8 }}>
              <input type="text" value={sec[`name_${L}`] || ""} onChange={(e) => updSection(si, { [`name_${L}`]: e.target.value })} placeholder={t("fwBuilder.sectionName")} disabled={!canEdit} data-testid={`fw-section-${si}-name`} />
              {canEdit && <button type="button" className="tb-funnel-del" onClick={() => delSection(si)} aria-label="del">×</button>}
            </div>
            <div style={{ marginTop: 8, paddingLeft: 12, borderLeft: "2px solid rgba(255,255,255,0.06)" }}>
              {(sec.attributes || []).map((it, ii) => {
                const f = itemForm(si, ii);
                return (
                  <div key={ii} className="tb-block" style={{ marginTop: 8 }} data-testid={`fw-item-${si}-${ii}`}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8 }}>
                      <input type="text" value={it[`name_${L}`] || ""} onChange={(e) => updItem(si, ii, { [`name_${L}`]: e.target.value })} placeholder={t("fwBuilder.itemName")} disabled={!canEdit} />
                      {canEdit && <button type="button" className="tb-funnel-del" onClick={() => delItem(si, ii)} aria-label="del">×</button>}
                    </div>
                    <div style={{ marginTop: 8 }}>
                      <div className="tb-muted" style={{ fontSize: "0.78rem", marginBottom: 4 }}>{t("fwBuilder.questions")}</div>
                      {(f.questions || []).map((q, qi) => (
                        <div key={qi} style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8, marginBottom: 6 }}>
                          <input type="text" value={q[`label_${L}`] || ""} onChange={(e) => updQuestion(si, ii, qi, { [`label_${L}`]: e.target.value })} placeholder={t("fwBuilder.questionLabel")} disabled={!canEdit} data-testid={`fw-q-${si}-${ii}-${qi}`} />
                          {canEdit && <button type="button" className="tb-funnel-del" onClick={() => delQuestion(si, ii, qi)} aria-label="del">×</button>}
                        </div>
                      ))}
                      {canEdit && <button type="button" className="tb-link-btn" onClick={() => addQuestion(si, ii)} data-testid={`fw-add-q-${si}-${ii}`}>+ {t("fwBuilder.addQuestion")}</button>}
                    </div>
                  </div>
                );
              })}
              {canEdit && <button type="button" className="tb-link-btn" onClick={() => addItem(si)} data-testid={`fw-add-item-${si}`}>+ {t("fwBuilder.addItem")}</button>}
            </div>
          </div>
        ))}
      </div>

      <div className="tb-block" style={{ marginTop: 14 }}>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <h3 style={{ marginTop: 0 }}>{t("fwBuilder.references")}</h3>
          {canEdit && <button type="button" className="tb-ghost-btn" onClick={addRef} data-testid="fw-add-ref">+ {t("fwBuilder.addReference")}</button>}
        </div>
        <p className="tb-muted" style={{ fontSize: "0.82rem", marginTop: 0 }}>{t("fwBuilder.referencesHint")}</p>
        {(doc.references || []).map((r, i) => (
          <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 2fr auto", gap: 8, marginBottom: 8 }} data-testid={`fw-ref-${i}`}>
            <input type="text" value={r.label || ""} onChange={(e) => updRef(i, { label: e.target.value })} placeholder={t("fwBuilder.refLabel")} disabled={!canEdit} />
            <input type="url" value={r.url || ""} onChange={(e) => updRef(i, { url: e.target.value })} placeholder="https://…" disabled={!canEdit} />
            {canEdit && <button type="button" className="tb-funnel-del" onClick={() => delRef(i)} aria-label="del">×</button>}
          </div>
        ))}
      </div>

      <div className="tb-block" style={{ marginTop: 14 }}>
        <h3 style={{ marginTop: 0 }}>{t("fwBuilder.legalNotice")}</h3>
        <p className="tb-muted" style={{ fontSize: "0.82rem", marginTop: 0 }}>{t("fwBuilder.legalHint")}</p>
        <div className="tb-form">
          <div><label>{t("fwBuilder.legalLabel")}</label><textarea rows={3} value={getL(doc, "legal_notice")} onChange={(e) => setL("legal_notice", e.target.value)} disabled={!canEdit} data-testid="fw-legal" /></div>
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, marginTop: 16, flexWrap: "wrap" }}>
        {canEdit && <button type="button" className="tb-primary-btn" onClick={save} disabled={busy} data-testid="fw-save">{busy ? t("auth.loading") : t("fwBuilder.save")}</button>}
        {!isNew && canEdit && <button type="button" className="tb-danger-btn" onClick={remove} disabled={busy} data-testid="fw-delete">{t("fwBuilder.deleteFramework")}</button>}
        <Link to="/app/frameworks" className="tb-ghost-btn" style={{ textDecoration: "none" }}>{t("book.cancel")}</Link>
      </div>
    </div>
  );
}
